import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
} from 'antd';
import {
  ReconciliationOutlined,
  ImportOutlined,
  DiffOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import { onError } from 'utils/handlers';
import PlainTransfer from 'components/PlainTransfer';
import Select from 'components/Select';
import {
  baseURI,
  deleteConceptoDeIngresos,
  getConceptoDeIngresos,
  getConceptosDeIngresos,
  patchConceptoDeIngresos,
  postConceptoDeIngresos,
} from 'api/catalogos/conceptosDeIngresos';
import { getFundamentosLegales } from 'api/configuracion/fundamentosLegales';
import { getNaturalezasDeIngreso, getFuentes } from 'api/configuracion/';
import { getClasificadoresEconomicos, getCatalogoDePadrones } from 'api/catalogos';
import { getTiposDeActualizaciones } from 'api/recaudacion/tiposDeActualizacion';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { getTiposDeRecargos } from 'api/recaudacion/tiposDeRecargo';
import { getClase, getClases } from 'api/catalogos/clases';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { isMutable } from 'utils/estadosGlobales';
import API from 'utils/api';

const { TabPane } = Tabs;

const catalogsModels = [
  'descuento',
  'recargo',
  'actualizacion',
  'naturalezadeingreso',
  'fundamentolegal',
  'fuente',
  'clasificadoreconomico',
  'padron',
  'clase',
];

// eslint-disable-next-line react/prop-types
const ConceptoDeIngreso = ({ permission }) => {
  const user = useSelector(({ auth }) => auth.user);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones);
  const fuentes = useSelector(({ catalogs }) => catalogs.fuentes);
  const naturalezasDeIngreso = useSelector(({ catalogs }) => catalogs.naturalezasDeIngreso);
  const clasificadoresEconomicos = useSelector(({ catalogs }) => catalogs.clasificadoresEconomicos);
  const [form] = Form.useForm();
  const [formContabilidad] = Form.useForm();
  const [formAccesorios] = Form.useForm();
  const isIngresoPresupeustal = Form.useWatch('es_ingreso_presupuestal', form);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [clasesTemp, setClasesTemp] = useState({ results: [] });
  const [tipoDeRecargo, setTipoDeRecargo] = useState([]);
  const [tipoActualizaciones, setTipoActualizaciones] = useState([]);
  const [fundamentos, setFundamentos] = useState([]);
  const [tiposDeDescuentos, setTiposDeDescuentos] = useState([]);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const clase = useRef({ results: [] });

  const fetchData = async (_page = 1, q) => {
    setCurrentPage(_page);
    setLoading(true);
    const _data = await getConceptosDeIngresos({
      ...q,
      page: _page,
    });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const dataDescuentos = await getTiposDeDescuentos({ es_descuento_especial: false });
      const dataTiposRecargos = await getTiposDeRecargos();
      const dataActualizaciones = await getTiposDeActualizaciones();
      const dataFundamentosLegales = await getFundamentosLegales();
      await getFuentes();
      await getCatalogoDePadrones();
      await getNaturalezasDeIngreso();
      await getClasificadoresEconomicos();
      if (mounted) {
        setTiposDeDescuentos(dataDescuentos);
        setTipoDeRecargo(dataTiposRecargos);
        setTipoActualizaciones(dataActualizaciones);
        setFundamentos(dataFundamentosLegales);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [periodoFiscalSelected]);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formContabilidad.resetFields();
    formAccesorios.resetFields();
    setCurrentTabKey('general');
    setSelectedRowKeys([]);
    setMutable(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formContabilidad.getFieldsValue(),
        ...formAccesorios.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchConceptoDeIngresos(key, values) : postConceptoDeIngresos(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form, formContabilidad, formAccesorios], setCurrentTabKey);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteConceptoDeIngresos(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    formContabilidad.setFieldsValue(record);
    formAccesorios.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const { cri_clase } = await getConceptoDeIngresos(key);
    if (cri_clase) {
      const matchCRI = await getClase(cri_clase);
      const output = {
        count: 1,
        results: [matchCRI],
      };
      setClasesTemp(output);
    }
    setVisible(true);
  };
  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 550,
    },
    {
      titleText: 'Naturaleza de Ingresos',
      dataIndex: 'naturaleza_de_ingresos',
      key: 'naturaleza_de_ingresos',
      width: 200,
      // render: (val) => (naturalezasDeIngreso.find((item) => item.id === val)?.descripcion),
      catalog: {
        cat: naturalezasDeIngreso,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Padrón',
      dataIndex: 'padron',
      key: 'padron',
      width: 150,
      catalog: {
        cat: padrones,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
      },
    },
  ];

  const createClave = () => {
    if (!selectedRowKeys.length) {
      let newClave;
      const clone = [...data.results];
      const { cri_clase } = form.getFieldsValue();
      const match = clase.current.results.find((c) => c.id === cri_clase);
      if (match) {
        newClave = `${match.clave}${clone.length}`;
        const matchConcepto = clone.find((e) => e.clave === newClave);
        if (matchConcepto) {
          newClave = `${match.clave}${clone.length + 1}`;
        }
        form.setFieldsValue({ clave: newClave });
      }
    }
  };

  const authorizeAll = async () => {
    setLoading(true);
    const promises = data.results.map((e) => API.patch(`${baseURI}${e.id}/`, { estados_globales: 4 }));
    await Promise.all(promises);
    await fetchData(currentPage);
    setLoading(false);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data?.results || []}
            handleOnRowClick={handleOnRowClick}
            rowSelection={rowSelection}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            customActions={[
              user?.email.includes('sigob') ? {
                icon: <CheckCircleOutlined />,
                text: 'Autorizar Todos',
                onClick: authorizeAll,
                disabledBySelection: false,
                hasPermission: true,
              } : {
                hasPermission: false,
              },
            ]}
            rowKey="id"
            baseURI={baseURI}
            permission={permission}
            mobileColIndex={0}
            allowImport
            hasAllViewPermission={hasAllViewPermission}
            pagination={false}
            paginationCallback={fetchData}
            count={data.count}
            currentPage={currentPage}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Concepto de Ingresos`}
            extra={(
              <FormSubmitControls
                baseURI={baseURI}
                loading={loading}
                mutable={mutable}
                onCancel={onCancel}
                onFinish={onFinish}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Tabs
              activeKey={currentTabKey}
              defaultActiveKey="general"
              onChange={setCurrentTabKey}
              type="card"
            >
              <TabPane
                tab={(
                  <span>
                    <ImportOutlined />
                    General
                  </span>
                )}
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  name="general"
                  form={form}
                  onFinish={() => onFinish()}
                  scrollToFirstError
                  initialValues={{
                    estados_globales: 1,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="es_ingreso_presupuestal"
                        label="¿Es ingreso presupuestal?"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    {!!isIngresoPresupeustal && (
                      <Col
                        xs={24}
                        sm={24}
                        md={8}
                      >
                        <Form.Item
                          rules={rules.required}
                          name="cri_clase"
                          label="CRI"
                          hasFeedback
                        >
                          <Select
                            onChange={createClave}
                            keyLabelRender
                            labelProp="denominacion"
                            dataSource={clasesTemp.results?.length ? clasesTemp : clase.current}
                            usingExternalDataSource={Boolean(clasesTemp.results?.length)}
                            pagination={{
                              getter: getClases,
                              setter: (dataSource) => {
                                clase.current = dataSource;
                              },
                              config: { auto: true, qName: 'denominacion' },
                            }}
                            onClear={() => {
                              if (!selectedRowKeys.length) form.resetFields(['clave']);
                              setClasesTemp({ results: [] });
                            }}
                          />
                        </Form.Item>
                      </Col>
                    )}

                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="clave"
                        label="Clave"
                        hasFeedback
                      >
                        <Input allowClear disabled={selectedRowKeys.length} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        rules={rules.required}
                        name="descripcion"
                        label="Descripción"
                        hasFeedback
                      >
                        <Input.TextArea allowClear autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="padron"
                        label="Padrón"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select dataSource={padrones} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          disabled={!selectedRowKeys.length}
                          dataSource={estadosGlobales}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>

              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <ReconciliationOutlined />
                    Contabilidad
                  </span>
                )}
                key="contabilidad"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={formContabilidad}
                  initialValues={{
                    fuente_de_ingreso: 3,
                    clasificador_economico: 1,
                    naturaleza_de_ingresos: 2,
                  }}
                >
                  <Row gutter={10}>
                    {!!isIngresoPresupeustal && (
                      <Col
                        xs={24}
                        sm={24}
                        md={8}
                      >
                        <Form.Item
                          name="fuente_de_ingreso"
                          label="Fuente de ingreso"
                          hasFeedback
                        >
                          <Select dataSource={fuentes} />
                        </Form.Item>
                      </Col>
                    )}

                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="naturaleza_de_ingresos"
                        label="Naturaleza de ingresos"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select dataSource={naturalezasDeIngreso} />
                      </Form.Item>
                    </Col>
                    {!!isIngresoPresupeustal && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe_presupuestado"
                            label="Importe Presupuestado"
                            hasFeedback
                            decimal
                            allowClear
                          />
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            name="fundamento_legal"
                            label="Fundamento legal"
                            hasFeedback
                          >
                            <Select
                              dataSource={fundamentos}
                              labelProp="denominacion"
                            />
                          </Form.Item>
                        </Col>

                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            name="clasificador_economico"
                            label="Clasificador económico"
                            hasFeedback
                          >
                            <Select dataSource={clasificadoresEconomicos} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="sub_cuenta_contable"
                        label="Cuenta contable"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Input allowClear />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DiffOutlined />
                    Accesorios
                  </span>
                )}
                key="3"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={formAccesorios}
                >
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tipoDeRecargo}
                      label="Tipos de recargos"
                      formItemName="tipos_de_recargo"
                      form={formAccesorios}
                      filterProp="descripcion"
                    />
                  </Col>
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tipoActualizaciones}
                      label="Tipos de actualización"
                      formItemName="tipos_de_actualizacion"
                      form={formAccesorios}
                      filterProp="descripcion"
                    />
                  </Col>
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tiposDeDescuentos}
                      label="Tipos de descuentos"
                      formItemName="tipos_de_descuento"
                      form={formAccesorios}
                      filterProp="descripcion"
                    />
                  </Col>

                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Concepto de Ingresos ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConceptoDeIngreso;
